import { UserAuthenticatedApiCall } from 'apps/sigma2-ui/src/state/user-types';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { UserAnalysisInformations } from 'libs/end-to-end/src/types/user-analysis/user-analysis-details';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { usePolling } from 'apps/sigma2-ui/src/lib/hooks/usePolling';
import { GenericSuccess } from 'libs/end-to-end/src/generic';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { isEqual } from 'lodash';
import { useUserObservationOrgAndDbIds } from 'apps/sigma2-ui/src/lib/hooks/useUserObservationOrgAndDbIds';

export const syncCurrentUserAnalyses = ({
  locallyDeletedUserIds,
  remoteUserAnalyses,
}: {
  locallyDeletedUserIds: string[];
  remoteUserAnalyses: UserAnalysisInformations;
}): UserAnalysisInformations => {
  const remoteUserAnalysesCopy = [...remoteUserAnalyses];

  for (const userId of locallyDeletedUserIds) {
    const userIndex = remoteUserAnalysesCopy.findIndex(
      (user) => user.id === userId,
    );
    if (userIndex !== -1) {
      remoteUserAnalysesCopy.splice(userIndex, 1);
    }
  }

  return remoteUserAnalysesCopy;
};

const getUserAnalysis = async ({
  userAuthenticatedApiCall,
  selectedOrganizationId,
  selectedDatabaseId,
  selectedDatabaseRevisionId,
  isEnabled,
}: {
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  selectedOrganizationId: string | null;
  selectedDatabaseId: string | null;
  selectedDatabaseRevisionId: string | null;
  isEnabled: boolean;
}) => {
  if (!isEnabled) {
    return null;
  }
  if (
    !selectedOrganizationId ||
    !selectedDatabaseId ||
    !selectedDatabaseRevisionId
  ) {
    return null;
  }
  const result = await userAuthenticatedApiCall({
    url: `${getApiBaseUrl()}/user-analyses/organizations/${selectedOrganizationId}/databases/${selectedDatabaseId}/revisions/${selectedDatabaseRevisionId}/analyses?originUrl=${window.location.origin}`,
    responseSchema: UserAnalysisInformations,
  });

  if (!result) {
    return null;
  }

  if (result.resultType === 'err') {
    return null;
  }

  return result.value;
};

export const useIsUserAnalysisEnabled = () => {
  const { featureFlags } = useUser();

  return useMemo(() => {
    return featureFlags?.dataQueuingUserAnalysis ?? false;
  }, [featureFlags]);
};

/**
 * Hook for user analysis. User Analysis is a feature that allows users to keep track of
 * their analysis results via toasts on the UI. Currently Custom Viz's are executed by users,
 * so this feature is used to keep track of the results of those Custom Viz's. The GET user analysis
 * endpoint is called every 8 seconds to check for new analysis results. It is also possible to manually
 * invoke the GET user analysis endpoint by using the useUserAnalysisSignaling hook, and in turn, this hook
 * listens for signals from the useUserAnalysisSignaling hook to manually invoke the GET user analysis endpoint.
 * This may be done to refresh the user analysis results when the user is actively looking at the UI. Because of this
 * a longer poll time was considered to reduce the number of calls to the backend, with the understanding that the signaling
 * mechanism can be used to manually refresh the user analysis results and therefore update only when necessary.
 * A user can interact with the toast and perform actions like seeing results, closing the toast, or retrying.
 * The user can also delete the toast, which will remove the toast from the UI and mark it as deleted in the backend.
 * The user can also retry the toast, which will re-execute the Custom Viz and show the toast again. A toast is automatically
 * added in the backend when a Custom Viz is executed.
 */
export const useUserAnalysis = () => {
  const { userAuthenticatedApiCall } = useUser();

  const {
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  } = useUserObservationOrgAndDbIds();

  const [loadedUserAnalyses, setLoadedUserAnalyses] =
    useState<UserAnalysisInformations | null>(null);

  const isUserAnalysisEnabled = useIsUserAnalysisEnabled();

  const [locallyDeletedUserAnalysisIds, setLocallyDeletedUserAnalysisIds] =
    useState<string[]>([]);

  const [didInitialLoad, setDidInitialLoad] = useState(false);

  const currentUserAnalyses = useMemo(() => {
    if (loadedUserAnalyses) {
      return syncCurrentUserAnalyses({
        locallyDeletedUserIds: locallyDeletedUserAnalysisIds,
        remoteUserAnalyses: loadedUserAnalyses,
      });
    }

    return null;
  }, [loadedUserAnalyses, locallyDeletedUserAnalysisIds]);

  const [manualCallInProgress, setManualCallInProgress] = useState(false);

  const onReceivedResult = useCallback(
    (data: UserAnalysisInformations | null) => {
      if (!isEqual(data, loadedUserAnalyses)) {
        setLoadedUserAnalyses(data);
      }
    },
    [loadedUserAnalyses],
  );

  const pollingAction = useMemo(() => {
    return async () => {
      return getUserAnalysis({
        userAuthenticatedApiCall,
        selectedDatabaseId,
        selectedDatabaseRevisionId,
        selectedOrganizationId,
        isEnabled: isUserAnalysisEnabled,
      });
    };
  }, [
    isUserAnalysisEnabled,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
    selectedOrganizationId,
    userAuthenticatedApiCall,
  ]);

  const pollingPayload = usePolling(
    useMemo(() => {
      return {
        action: pollingAction,
        onReceivedResult,
        delay: 8000,
      };
    }, [onReceivedResult, pollingAction]),
  );

  const handleManualFetch = useAsyncCallback(
    async (isAlive) => {
      if (!isAlive()) {
        return;
      }

      if (manualCallInProgress) return;

      setManualCallInProgress(true);

      pollingPayload.pause();

      const result = await getUserAnalysis({
        userAuthenticatedApiCall,
        selectedDatabaseId,
        selectedDatabaseRevisionId,
        selectedOrganizationId,
        isEnabled: isUserAnalysisEnabled,
      });

      setLoadedUserAnalyses(result);

      pollingPayload.resume();

      setManualCallInProgress(false);
    },
    [
      isUserAnalysisEnabled,
      manualCallInProgress,
      pollingPayload,
      selectedDatabaseId,
      selectedDatabaseRevisionId,
      selectedOrganizationId,
      userAuthenticatedApiCall,
    ],
  );

  useEffect(() => {
    if (didInitialLoad) {
      return;
    }

    if (!isUserAnalysisEnabled) {
      return;
    }

    if (
      selectedOrganizationId &&
      selectedDatabaseId &&
      selectedDatabaseRevisionId
    ) {
      handleManualFetch();
      setDidInitialLoad(true);
    }
  }, [
    didInitialLoad,
    handleManualFetch,
    isUserAnalysisEnabled,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
    selectedOrganizationId,
  ]);

  /**
   * This removes the toast from the API and UI. It won't do any logic to cancel
   * the currently running custom viz request or falling back to the initial
   * dropdown state
   * @see onUserAnalysisCancel
   */
  const onDeleteUserAnalysis = useAsyncCallback(
    async (
      isAlive,
      {
        analysisId,
      }: {
        analysisId: string;
      },
    ) => {
      if (!isAlive() || !selectedOrganizationId) {
        return;
      }

      setLocallyDeletedUserAnalysisIds((prev) => [...prev, analysisId]);

      const result = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/user-analyses/organizations/${selectedOrganizationId}/databases/${selectedDatabaseId}/revisions/${selectedDatabaseRevisionId}/analyses/${encodeURIComponent(analysisId)}?originUrl=${window.location.origin}`,
        responseSchema: UserAnalysisInformations,
        requestOptions: {
          method: 'DELETE',
        },
      });

      if (!result) {
        return;
      }

      if (result.resultType === 'err') {
        return;
      }

      setLoadedUserAnalyses(result.value);
    },
    [
      selectedDatabaseId,
      selectedDatabaseRevisionId,
      selectedOrganizationId,
      userAuthenticatedApiCall,
    ],
  );

  const onRetryUserAnalysis = useAsyncCallback(
    async (isAlive, customViz: CustomVizRequest) => {
      if (!isAlive()) {
        return;
      }

      const result = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/v2/custom-viz/viz`,
        responseSchema: GenericSuccess,
        requestOptions: {
          body: JSON.stringify(customViz),
          method: 'POST',
        },
      });

      if (!result) {
        return;
      }

      if (result.resultType === 'err') {
        return;
      }

      handleManualFetch();
    },
    [handleManualFetch, userAuthenticatedApiCall],
  );

  return useMemo(() => {
    return {
      currentUserAnalyses: currentUserAnalyses,
      onDeleteUserAnalysis,
      onRetryUserAnalysis,
      handleManualFetch,
    };
  }, [
    currentUserAnalyses,
    onDeleteUserAnalysis,
    onRetryUserAnalysis,
    handleManualFetch,
  ]);
};
